/**
 * What the settings editor remembers per surface in StreamRift mode (2026-10-20): whether Advanced was open and which
 * knob groups were — so a trader who works in "While Pinnacle is off" on the ticket finds it open on the next game.
 * Null = never chosen, and the editor keeps today's rule (Advanced closed, the first two groups open). Per browser,
 * raw localStorage in try/catch (the BotModeSwitch.readTab pattern): unreadable storage is "never chosen", never an
 * error. A group title the catalog no longer has is dropped on read.
 */
import { KNOB_GROUPS, knobGroupsFor, type KnobStrategy } from './knobs';

export type PenelopeSurface = 'ticket' | 'popover' | 'desk' | 'defaults';
export interface EditorMemory { advanced: boolean | null; groups: string[] | null }

export const EMPTY_EDITOR_MEMORY: EditorMemory = { advanced: null, groups: null };
/** Per surface AND per strategy: a mini-game row shows other groups than a Penelope row, so what was open on one must
 *  not decide the other (review 10-20). The Penelope key keeps the plain surface name. */
export const editorMemoryKey = (surface: PenelopeSurface, strategy: KnobStrategy = 'penelope'): string =>
  strategy === 'penelope' ? `g1.penelope.editor.${surface}` : `g1.penelope.editor.${surface}.${strategy}`;

const TITLES: ReadonlySet<string> = new Set(KNOB_GROUPS.map((g) => g.title));

export function readEditorMemory(surface: PenelopeSurface, strategy: KnobStrategy = 'penelope'): EditorMemory {
  try {
    const raw = localStorage.getItem(editorMemoryKey(surface, strategy));
    if (!raw) return EMPTY_EDITOR_MEMORY;
    const v = JSON.parse(raw) as { advanced?: unknown; groups?: unknown };
    if (typeof v !== 'object' || v === null) return EMPTY_EDITOR_MEMORY;
    // Only titles this strategy's rows show: a foreign title never rides along.
    const shown = new Set(knobGroupsFor(strategy).map((g) => g.title));
    return {
      advanced: typeof v.advanced === 'boolean' ? v.advanced : null,
      groups: Array.isArray(v.groups) ? v.groups.filter((t): t is string => typeof t === 'string' && TITLES.has(t) && shown.has(t)) : null,
    };
  } catch { return EMPTY_EDITOR_MEMORY; }
}

export function writeEditorMemory(surface: PenelopeSurface, m: EditorMemory, strategy: KnobStrategy = 'penelope'): void {
  try { localStorage.setItem(editorMemoryKey(surface, strategy), JSON.stringify(m)); } catch { /* per-viewer convenience only */ }
}

/** Today's rule, named: with nothing remembered the first two groups of the row's strategy are open. */
export function defaultOpenGroups(strategy: KnobStrategy = 'penelope'): string[] {
  return knobGroupsFor(strategy).slice(0, 2).map((g) => g.title);
}
